import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

// Generate a full color palette from a single hex color
function hexToHSL(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255
  let g = parseInt(hex.slice(3, 5), 16) / 255
  let b = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h, s, l = (max + min) / 2
  if (max === min) { h = s = 0 }
  else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) }
}

function generatePalette(hex) {
  const { h, s, l } = hexToHSL(hex)
  return {
    50:  `hsl(${h}, ${Math.min(s + 20, 100)}%, 97%)`,
    100: `hsl(${h}, ${Math.min(s + 15, 100)}%, 93%)`,
    200: `hsl(${h}, ${Math.min(s + 10, 100)}%, 86%)`,
    300: `hsl(${h}, ${Math.min(s + 5, 100)}%, 75%)`,
    400: `hsl(${h}, ${s}%, 64%)`,
    500: `hsl(${h}, ${s}%, ${l}%)`,
    600: `hsl(${h}, ${Math.min(s + 5, 100)}%, ${Math.max(l - 10, 20)}%)`,
    700: `hsl(${h}, ${Math.min(s + 10, 100)}%, ${Math.max(l - 18, 15)}%)`,
    800: `hsl(${h}, ${Math.min(s + 15, 100)}%, ${Math.max(l - 25, 10)}%)`,
    900: `hsl(${h}, ${Math.min(s + 15, 100)}%, ${Math.max(l - 32, 8)}%)`,
  }
}

function applyCSSVariables(hex) {
  const palette = generatePalette(hex)
  const root = document.documentElement
  Object.entries(palette).forEach(([shade, value]) => {
    root.style.setProperty(`--color-primary-${shade}`, value)
  })
  // Also set the raw hex
  root.style.setProperty('--color-primary-hex', hex)
}

// Default services definition
const DEFAULT_SERVICES = [
  { key: 'ai_chatbot', label: 'AI Chatbot', description: 'RAG-powered product chat with BGE embeddings', icon: 'bot', enabled: true },
  { key: 'livekit', label: 'LiveKit Calls', description: 'Voice and video calling via LiveKit', icon: 'phone', enabled: true },
  { key: 'pos', label: 'POS System', description: 'Point of Sale terminal for cashier operations', icon: 'monitor', enabled: true },
  { key: 'social_media', label: 'Social Media', description: 'Social media integration and monitoring', icon: 'share2', enabled: true },
  { key: 'hr', label: 'HR Module', description: 'Human resources and attendance management', icon: 'users', enabled: true },
  { key: 'stock', label: 'Stock Management', description: 'Inventory and warehouse management', icon: 'package', enabled: true },
  { key: 'storefront', label: 'Vendor Storefront', description: 'Public-facing vendor pages and product catalog', icon: 'store', enabled: true },
  { key: 'notifications', label: 'Notifications', description: 'Push notifications and alerts', icon: 'bell', enabled: true },
  { key: 'translations', label: 'Multi-Language', description: 'Multi-language translation support', icon: 'languages', enabled: true },
  { key: 'subscriptions', label: 'Subscriptions', description: 'Tenant subscription plans and limits', icon: 'creditcard', enabled: true },
]

export const useAppStore = defineStore('app', () => {
  const sidebarCollapsed = ref(false)
  const loading = ref(false)
  const pageTitle = ref('')

  // Brand settings
  const brand = ref({
    app_name: localStorage.getItem('admin_brand_name') || 'Admin Dashboard',
    logo_url: localStorage.getItem('admin_brand_logo') || '',
    primary_color: localStorage.getItem('admin_brand_color') || '#3b82f6',
  })

  // App services - loaded from localStorage or defaults
  const services = ref(
    JSON.parse(localStorage.getItem('admin_services') || 'null') || DEFAULT_SERVICES
  )

  const theme = computed(() => localStorage.getItem('admin-theme') || 'light')
  const appName = computed(() => brand.value.app_name || 'Admin Dashboard')
  const appLogo = computed(() => brand.value.logo_url || '')

  // Check if a service is enabled
  function isServiceEnabled(key) {
    const svc = services.value.find(s => s.key === key)
    return svc ? svc.enabled : true
  }

  function setBrand(data) {
    if (data.app_name !== undefined) {
      brand.value.app_name = data.app_name
      localStorage.setItem('admin_brand_name', data.app_name)
    }
    if (data.logo_url !== undefined) {
      brand.value.logo_url = data.logo_url
      localStorage.setItem('admin_brand_logo', data.logo_url)
    }
    if (data.primary_color !== undefined) {
      brand.value.primary_color = data.primary_color
      localStorage.setItem('admin_brand_color', data.primary_color)
      applyCSSVariables(data.primary_color)
    }
  }

  function toggleService(key) {
    const svc = services.value.find(s => s.key === key)
    if (svc) {
      svc.enabled = !svc.enabled
      localStorage.setItem('admin_services', JSON.stringify(services.value))
    }
  }

  function setServices(data) {
    services.value = data
    localStorage.setItem('admin_services', JSON.stringify(data))
  }

  function toggleSidebar() {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }

  function toggleTheme() {
    const newTheme = theme.value === 'dark' ? 'light' : 'dark'
    localStorage.setItem('admin-theme', newTheme)
    document.documentElement.classList.toggle('dark', newTheme === 'dark')
  }

  function setLoading(val) { loading.value = val }
  function setPageTitle(title) { pageTitle.value = title }

  // Apply theme color on store init
  applyCSSVariables(brand.value.primary_color)

  return {
    sidebarCollapsed,
    loading,
    pageTitle,
    brand,
    services,
    theme,
    appName,
    appLogo,
    isServiceEnabled,
    setBrand,
    toggleService,
    setServices,
    toggleSidebar,
    toggleTheme,
    setLoading,
    setPageTitle,
  }
})
